/**
 * Merge two reads when the bytes between them cost less than another round trip.
 * At a few hundred ms per round trip, a few hundred KB of waste is cheaper than a new request.
 */
const MAX_GAP_BYTES = 256 * 1024;
/** Cap merged requests so one slow response can't hold back too many tiles. */
const MAX_SPAN_BYTES = 4 * 1024 * 1024;

type PendingRead = {
  start: number;
  /** Exclusive. */
  end: number;
  signal?: AbortSignal;
  resolve: (bytes: Uint8Array) => void;
  reject: (err: unknown) => void;
  settled: boolean;
  onAbort?: () => void;
};

function abortError(signal?: AbortSignal): unknown {
  return signal?.reason ?? new DOMException('Aborted', 'AbortError');
}

function settle(read: PendingRead, outcome: { bytes: Uint8Array } | { error: unknown }): boolean {
  if (read.settled) {
    return false;
  }
  read.settled = true;
  if (read.onAbort) {
    read.signal?.removeEventListener('abort', read.onAbort);
  }
  if ('bytes' in outcome) {
    read.resolve(outcome.bytes);
  } else {
    read.reject(outcome.error);
  }
  return true;
}

/**
 * Coalescing HTTP range reader.
 *
 * Reads requested in the same task are collected, sorted by offset and merged into as few
 * range requests as possible. COG tiles are stored row-major with the mask right after its
 * tile, so a visible row of tiles (image + mask) usually becomes a single request.
 * The body is streamed and each read resolves as soon as its bytes have arrived.
 */
export class CogRangeReader {
  private queue: PendingRead[] = [];
  private flushScheduled = false;

  constructor(private readonly url: string) {}

  read(start: number, end: number, signal?: AbortSignal): Promise<Uint8Array> {
    if (signal?.aborted) {
      return Promise.reject(abortError(signal));
    }
    return new Promise<Uint8Array>((resolve, reject) => {
      this.queue.push({ start, end, signal, resolve, reject, settled: false });
      if (!this.flushScheduled) {
        this.flushScheduled = true;
        queueMicrotask(() => this.flush());
      }
    });
  }

  private flush(): void {
    this.flushScheduled = false;
    const reads = this.queue.filter((r) => {
      if (r.signal?.aborted) {
        settle(r, { error: abortError(r.signal) });
        return false;
      }
      return true;
    });
    this.queue = [];
    reads.sort((a, b) => a.start - b.start);

    let group: PendingRead[] = [];
    let groupStart = 0;
    let groupEnd = 0;
    for (const read of reads) {
      const fits =
        group.length > 0 &&
        read.start - groupEnd <= MAX_GAP_BYTES &&
        Math.max(groupEnd, read.end) - groupStart <= MAX_SPAN_BYTES;
      if (fits) {
        group.push(read);
        groupEnd = Math.max(groupEnd, read.end);
        continue;
      }
      if (group.length) {
        void this.fetchGroup(group, groupStart, groupEnd);
      }
      group = [read];
      groupStart = read.start;
      groupEnd = read.end;
    }
    if (group.length) {
      void this.fetchGroup(group, groupStart, groupEnd);
    }
  }

  private async fetchGroup(reads: PendingRead[], start: number, end: number): Promise<void> {
    // The request is only aborted once every read in it has been cancelled
    const controller = new AbortController();
    let alive = reads.length;
    for (const read of reads) {
      if (!read.signal) {
        continue;
      }
      read.onAbort = () => {
        if (settle(read, { error: abortError(read.signal) }) && --alive === 0) {
          controller.abort();
        }
      };
      read.signal.addEventListener('abort', read.onAbort, { once: true });
    }

    try {
      const response = await fetch(this.url, {
        headers: { Range: `bytes=${start}-${end - 1}` },
        signal: controller.signal,
      });
      // A 200 is the whole file, which only lines up with our buffer when reading from the start
      const usable = response.status === 206 || (response.status === 200 && start === 0);
      if (!usable || !response.body) {
        throw new Error(`COG range request failed: ${response.status} ${response.statusText}`);
      }

      const buffer = new Uint8Array(end - start);
      let received = 0;
      // Reads are sorted by start, so resolve them in order as the body streams in
      let next = 0;
      const reader = response.body.getReader();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        // Servers may send more than asked for (a 200 with the whole file); keep what we need
        const chunk = value.subarray(0, buffer.length - received);
        buffer.set(chunk, received);
        received += chunk.length;

        while (next < reads.length && reads[next].end - start <= received) {
          const read = reads[next++];
          settle(read, { bytes: buffer.subarray(read.start - start, read.end - start) });
        }
        if (received === buffer.length) {
          // Every read is settled; don't download the rest of an oversized response
          void reader.cancel();
          break;
        }
      }

      for (const read of reads) {
        settle(read, { error: new Error('COG range response ended early') });
      }
    } catch (err) {
      for (const read of reads) {
        settle(read, { error: err });
      }
    }
  }
}
